/**
 * Product Card Swiper
 * Optimized for handling 50+ Swiper instances efficiently
 * Uses Intersection Observer for lazy initialization
 */

class ProductCardSwiperManager {
  constructor() {
    this.swipers = new Map();
    this.observer = null;
    this.isInitialized = false;

    // Bind methods
    this.init = this.init.bind(this);
    this.initSwiper = this.initSwiper.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.cleanup = this.cleanup.bind(this);

    // Auto-initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.init);
    } else {
      this.init();
    }
  }

  init() {
    if (this.isInitialized) return;

    try {
      // Setup Intersection Observer for lazy loading
      this.observer = new IntersectionObserver(this.handleIntersection, {
        rootMargin: '50px',
        threshold: 0.1
      });

      // Find all product card swipers and observe them
      const swiperElements = document.querySelectorAll('.product-card-swiper');
      swiperElements.forEach(element => {
        // Only observe elements with more than 1 slide
        const slides = element.querySelectorAll('.swiper-slide');
        if (slides.length > 1) {
          this.observer.observe(element);
        }
      });

      this.isInitialized = true;

      // Cleanup on page unload
      window.addEventListener('beforeunload', this.cleanup);

    } catch (error) {
      console.warn('ProductCardSwiperManager initialization failed:', error);
    }
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const swiperElement = entry.target;
        const productId = swiperElement.dataset.productId;

        // Only initialize if not already done
        if (!this.swipers.has(productId)) {
          this.initSwiper(swiperElement, productId);
        }

        // Stop observing this element
        this.observer.unobserve(swiperElement);
      }
    });
  }

  initSwiper(swiperElement, productId) {
    try {
      // Check if Swiper is available
      if (typeof Swiper === 'undefined') {
        console.warn('Swiper is not loaded');
        return;
      }

      const swiper = new Swiper(swiperElement, {
        // Core settings
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 300,

        // Performance optimizations
        watchSlidesProgress: false,
        watchSlidesVisibility: false,
        preventInteractionOnTransition: true,
        touchReleaseOnEdges: true,

        // Pagination
        pagination: {
          el: swiperElement.querySelector('.swiper-pagination'),
          type: 'bullets',
          clickable: true,
          dynamicBullets: false,
          hideOnClick: false,
        },

        // Touch settings
        touchRatio: 1,
        touchAngle: 45,
        simulateTouch: true,
        allowTouchMove: true,

        // Navigation arrows
        navigation: {
          nextEl: swiperElement.querySelector('.swiper-button-next'),
          prevEl: swiperElement.querySelector('.swiper-button-prev'),
        },
        scrollbar: false,
        keyboard: false,
        mousewheel: false,
        autoplay: false,
        loop: true,

        // Lazy loading (if needed)
        lazy: {
          loadPrevNext: false,
          loadOnTransitionStart: false,
        },

        // Accessibility
        a11y: {
          enabled: true,
          prevSlideMessage: 'Previous image',
          nextSlideMessage: 'Next image',
          paginationBulletMessage: 'Go to slide {{index}}',
        },

        // Events for cleanup
        on: {
          init: function() {
            // Add initialized class
            swiperElement.classList.add('swiper-initialized');
          },
          destroy: function() {
            // Remove initialized class
            swiperElement.classList.remove('swiper-initialized');
          }
        }
      });

      // Store swiper instance
      this.swipers.set(productId, swiper);

    } catch (error) {
      console.warn(`Failed to initialize swiper for product ${productId}:`, error);
    }
  }

  // Public method to manually initialize a specific swiper
  initializeSwiperById(productId) {
    const element = document.querySelector(`[data-product-id="${productId}"]`);
    if (element && !this.swipers.has(productId)) {
      this.initSwiper(element, productId);
    }
  }

  // Public method to destroy a specific swiper
  destroySwiperById(productId) {
    const swiper = this.swipers.get(productId);
    if (swiper) {
      swiper.destroy(true, true);
      this.swipers.delete(productId);
    }
  }

  // Clean up all swipers and observers
  cleanup() {
    try {
      // Destroy all swipers
      this.swipers.forEach((swiper, productId) => {
        swiper.destroy(true, true);
      });
      this.swipers.clear();

      // Disconnect observer
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      this.isInitialized = false;
    } catch (error) {
      console.warn('ProductCardSwiperManager cleanup failed:', error);
    }
  }

  // Clean up swipers for products that no longer exist in DOM
  cleanupRemovedSwipers() {
    try {
      const swipersToRemove = [];
      
      this.swipers.forEach((swiper, productId) => {
        const element = document.querySelector(`[data-product-id="${productId}"]`);
        if (!element) {
          // Element no longer exists in DOM, destroy the swiper
          swiper.destroy(true, true);
          swipersToRemove.push(productId);
        }
      });
      
      // Remove destroyed swipers from the map
      swipersToRemove.forEach(productId => {
        this.swipers.delete(productId);
      });
      
    } catch (error) {
      console.warn('ProductCardSwiperManager cleanupRemovedSwipers failed:', error);
    }
  }

  // Public method to reinitialize (useful for dynamic content)
  reinitialize() {
    this.cleanup();
    this.init();
  }
}

// Initialize the manager
window.productCardSwiperManager = new ProductCardSwiperManager();

// Expose public methods globally for theme integration
window.initProductCardSwiper = (productId) => {
  window.productCardSwiperManager.initializeSwiperById(productId);
};

window.destroyProductCardSwiper = (productId) => {
  window.productCardSwiperManager.destroySwiperById(productId);
};

window.reinitializeProductCardSwipers = () => {
  window.productCardSwiperManager.reinitialize();
};

window.cleanupRemovedSwipers = () => {
  window.productCardSwiperManager.cleanupRemovedSwipers();
};
